/**
 * View Component: MobileFilterDrawer
 * Bottom sheet filter panel for mobile.
 * Contains search + date + book + hotspot filters.
 *
 * Props:
 *   open: boolean
 *   filters: { search, book, date, hotspot, tag }
 *   bookList: [{ name, count }]
 *   dateList: [{ date, count }]
 *   hotspotList: [{ name, count }]
 *   onClose: () => void
 *   onFilterChange: (key, value) => void
 *   onClear: () => void
 *
 * ── T-Cron-2026-10-27 08:00 早间 — 改进清单 #10「无障碍」─────────────────
 * 🔴 本轮实测发现的**桌面/移动不对等**:
 *   桌面侧栏 Sidebar.jsx 的每一颗筛选 chip 都带 `aria-pressed`
 *   (T-Cron-2026-10-25 补的),但本文件的 chip **一颗都没有** ——
 *   唯一例外是「标签」分区(T-Cron-2026-10-26 02:00 单独补过)。
 *
 *   ⇒ 手机端读屏用户点一颗 chip,听屏只念「A版 · 热点主导 按钮」,
 *     **听不出这颗有没有被选中**。选中状态只有视觉上的红色
 *     (.mobile-filter-chip.active),读屏完全拿不到 ⇒
 *     手机端是「看得见、听不见」。
 *
 *   桌面/移动两套筛选 UI 表达同一份状态,契约必须一样 ⇒ 本轮补齐:
 *     · 版本 3 颗 + 批次 + 分类 + 书籍 + 热点 + 日期 ⇒ 全部 aria-pressed
 *     · 每颗 chip 的 aria-label 带上**命中篇数**(「深度工作，12篇」)
 *       —— 只念 chip 名的话,用户不知道该点哪一颗(1257 颗 chip 的
 *       移动端抽屉里这是刚需)。
 *     · 「清除全部筛选」补 aria-label,与 Sidebar 同款措辞。
 *
 *   ⚠️ 视觉零回归:aria-pressed / aria-label 都是无样式属性,
 *      不改变任何 className ⇒ CSS 一行都不用动。
 *
 * ── T-Cron-2026-11-01 08:00 早间 — 改进清单 #15「标签云」────────────────
 * 手机端的标签分区也换成**标签云**,和桌面侧栏保持同一套档位
 * (档位计算共用 harness/tagCloud.js ⇒ 两边不会出现「同样的标签
 * 在手机上是一个字号、在桌面上是另一个字号」的割裂)。
 *
 * 手机端为什么尤其需要档位:
 *   抽屉里的标签芯片是**平铺的换行流**,25 颗等大芯片会让抽屉
 *   长到需要滚动才能看完。字号分档后,主力标签(读书笔记 / 认知升级…)
 *   一眼可辨,用户通常直接点得到自己要的,不用翻。
 */

import { renderTagCloud } from '../harness/tagCloud.js';

function SearchIconMobile2() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-tertiary)' }} aria-hidden="true" focusable="false">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );
}

function MobileFilterDrawer({ open, filters, bookList, dateList, hotspotList, categoryList, batchList, tagList, onClose, onFilterChange, onClear, searchInputRef, searchHistory, onCommitSearch, onRemoveSearchHistory, onClearSearchHistory }) {
  const hasActiveFilter = filters.search || filters.book || filters.date || filters.hotspot || filters.category || filters.version || filters.batch || filters.tag;

  // T-Cron-2026-10-24 08:00:搜索历史(改进清单 #15)。与侧栏同口径。
  const showHistory = !!searchHistory && searchHistory.length > 0 && !filters.search;

  const commitSearch = (q) => {
    onFilterChange('search', q);
    if (onCommitSearch) onCommitSearch(q);
  };

  // T-Cron-2026-11-01 08:00 — 改进清单 #15「标签云」。
  // 与桌面侧栏共用 harness/tagCloud.js 的档位计算 ⇒ 两边字号一致。
  const tagCloudItems = React.useMemo(
    () => renderTagCloud({
      tagList,
      activeTag: filters.tag,
      onSelect: (name) => onFilterChange('tag', filters.tag === name ? '' : name),
      chipClass: 'mobile-filter-chip',
    }),
    [tagList, filters.tag, onFilterChange]
  );

  // T-Cron-2026-10-22 08:00 早间 (round-1-of-day) — a11y #10:
  // 这个抽屉之前连 role="dialog" 都没有 —— 读屏用户根本不知道弹出了一个
  // 模态层,Tab 也会直接跑到背后的看板卡片上。
  // 现在补 role/aria-modal + 焦点陷阱 + Esc 关闭。
  const drawerRef = React.useRef(null);
  useFocusTrap(drawerRef, { enabled: open, autoFocus: true });

  React.useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      // 🔴 T137 (2026-11-03 08:00 早间) — 长按(auto-repeat)守卫。
      // 按住 Esc 不放 ⇒ 反复触发 onClose(本无害),但同一个按键会**继续冒泡**
      // 到全局 handler ⇒ 统一在入口挡掉单键 repeat。
      if (e.repeat && !(e.ctrlKey || e.metaKey || e.altKey)) return;
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  return (
    <>
      <div
        className={`mobile-filter-overlay ${open ? 'open' : ''}`}
        onClick={onClose}
      ></div>
      <div
        ref={drawerRef}
        className={`mobile-filter-drawer ${open ? 'open' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="筛选"
        tabIndex={-1}
      >
        <div className="mobile-filter-header">
          <span className="mobile-filter-title">筛选</span>
          <button className="mobile-filter-close" onClick={onClose} aria-label="关闭筛选">
            <CloseIcon />
          </button>
        </div>

        <div className="mobile-filter-body">
          {/* Search */}
          <div>
            <div className="mobile-filter-section-title">搜索</div>
            <div className="mobile-search-box">
              <SearchIconMobile2 />
              <input
                type="text"
                placeholder="搜索标题、书名、标签或拼音首字母"
                value={filters.search}
                onChange={(e) => onFilterChange('search', e.target.value)}
                onKeyDown={(e) => {
                  // 🔴 T137 (2026-11-03 08:00 早间) — 长按(auto-repeat)守卫。
                  // 按住 Enter 不放 ⇒ 反复 commitSearch ⇒ 搜索历史里塞进
                  //   一串重复记录(改进清单 #15「搜索历史」直接被污染)。
                  if (e.repeat && !(e.ctrlKey || e.metaKey || e.altKey)) return;
                  // Enter = 提交查询 ⇒ 记进历史(改进清单 #15)。
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitSearch(filters.search);
                  }
                }}
                ref={searchInputRef}
                aria-label="搜索选题，支持中文或拼音首字母"
              />
            </div>
            {/* T-Cron-2026-10-24 08:00:搜索历史(改进清单 #15)。
                移动端是主要使用场景 ⇒ 历史必须在抽屉里也有。
                和侧栏一致:输入框空着才显示。 */}
            {showHistory && (
              <div className="search-history mobile-search-history" role="group" aria-label="最近搜索">
                <div className="search-history-head">
                  <span className="search-history-title">最近搜索</span>
                  <button
                    className="search-history-clear"
                    onClick={onClearSearchHistory}
                    aria-label="清空搜索历史"
                    title="清空搜索历史"
                  >
                    清空
                  </button>
                </div>
                <ul className="search-history-list">
                  {searchHistory.map((q) => (
                    <li key={q} className="search-history-item">
                      <button
                        className="search-history-query"
                        onClick={() => commitSearch(q)}
                        aria-label={`搜索 ${q}`}
                        title={q}
                      >
                        {q}
                      </button>
                      <button
                        className="search-history-remove"
                        onClick={() => onRemoveSearchHistory(q)}
                        aria-label={`删除搜索历史 ${q}`}
                        title="删除"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" focusable="false">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Version */}
          <div>
            <div className="mobile-filter-section-title">
              版本
              {filters.version && (
                <span style={{ color: 'var(--text-accent)', marginLeft: 8, fontSize: 12 }}>
                  · 已选 1 项
                </span>
              )}
            </div>
            <div className="mobile-filter-chips">
              <button
                className={`mobile-filter-chip ${filters.version === 'A' ? 'active' : ''}`}
                onClick={() => onFilterChange('version', filters.version === 'A' ? '' : 'A')}
                aria-pressed={filters.version === 'A'}
              >
                A版 · 热点主导
              </button>
              <button
                className={`mobile-filter-chip ${filters.version === 'B' ? 'active' : ''}`}
                onClick={() => onFilterChange('version', filters.version === 'B' ? '' : 'B')}
                aria-pressed={filters.version === 'B'}
              >
                B版 · 书籍点睛
              </button>
              <button
                className={`mobile-filter-chip ${filters.version === 'C' ? 'active' : ''}`}
                onClick={() => onFilterChange('version', filters.version === 'C' ? '' : 'C')}
                aria-pressed={filters.version === 'C'}
              >
                C版 · 书籍主导
              </button>
            </div>
          </div>

          {/* Batch */}
          {batchList && batchList.length > 0 && (
            <div>
              <div className="mobile-filter-section-title">
                批次
                {filters.batch && (
                  <span style={{ color: 'var(--text-accent)', marginLeft: 8, fontSize: 12 }}>
                    · 已选 1 项
                  </span>
                )}
              </div>
              <div className="mobile-filter-chips">
                {batchList.map(({ name, count }) => (
                  <button
                    key={name}
                    className={`mobile-filter-chip ${filters.batch === name ? 'active' : ''}`}
                    onClick={() => onFilterChange('batch', filters.batch === name ? '' : name)}
                    aria-pressed={filters.batch === name}
                    aria-label={`${name}，${count}篇`}
                  >
                    {name}
                    <span style={{ opacity: 0.6, marginLeft: 4, fontSize: 11 }}>{count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Category */}
          {categoryList && categoryList.length > 0 && (
            <div>
              <div className="mobile-filter-section-title">
                分类
                {filters.category && (
                  <span style={{ color: 'var(--text-accent)', marginLeft: 8, fontSize: 12 }}>
                    · 已选 1 项
                  </span>
                )}
              </div>
              <div className="mobile-filter-chips">
                {categoryList.map(({ name, count }) => (
                  <button
                    key={name}
                    className={`mobile-filter-chip ${filters.category === name ? 'active' : ''}`}
                    onClick={() => onFilterChange('category', filters.category === name ? '' : name)}
                    aria-pressed={filters.category === name}
                    aria-label={`${name}，${count}篇`}
                  >
                    {name}
                    <span style={{ opacity: 0.6, marginLeft: 4, fontSize: 11 }}>{count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Books */}
          <div>
            <div className="mobile-filter-section-title">
              书籍
              {filters.book && (
                <span style={{ color: 'var(--text-accent)', marginLeft: 8, fontSize: 12 }}>
                  · 已选 1 项
                </span>
              )}
            </div>
            <div className="mobile-filter-chips">
              {bookList.map(({ name, count }) => (
                <button
                  key={name || '未分类'}
                  className={`mobile-filter-chip ${filters.book === name ? 'active' : ''}`}
                  onClick={() => onFilterChange('book', filters.book === name ? '' : name)}
                  aria-pressed={filters.book === name}
                  aria-label={`${name || '未分类'}，${count}篇`}
                >
                  {name || '未分类'}
                </button>
              ))}
            </div>
          </div>

          {/* Hotspots */}
          <div>
            <div className="mobile-filter-section-title">
              热点
              {filters.hotspot && (
                <span style={{ color: 'var(--text-accent)', marginLeft: 8, fontSize: 12 }}>
                  · 已选 1 项
                </span>
              )}
            </div>
            <div className="mobile-filter-chips">
              {hotspotList.map(({ name, count }) => (
                <button
                  key={name}
                  className={`mobile-filter-chip ${filters.hotspot === name ? 'active' : ''}`}
                  onClick={() => onFilterChange('hotspot', filters.hotspot === name ? '' : name)}
                  aria-pressed={filters.hotspot === name}
                  aria-label={`${name.length > 12 ? name.slice(0, 12) + '…' : name}，${count}篇`}
                  style={{ fontSize: 12 }}
                >
                  {name.length > 12 ? name.slice(0, 12) + '…' : name}
                </button>
              ))}
            </div>
          </div>

          {/* Tags — T-Cron-2026-10-26 02:00:改进清单 #15/#10「移动端筛选对齐」。
              🔴 本轮实测发现的**功能不对等**:桌面侧栏有「热门标签」
              (25 颗 chip,store.tagList),但 MobileFilterDrawer **完全没有**
              标签分区 —— 而 ≤768px 时 .app-sidebar 是 display:none,
              ⇒ 手机端用户**根本无法按标签筛选**,桌面端却有。
              数据层一直是通的(getTagList/filterByTag/setFilter('tag',…)),
              纯粹是移动端漏传了 tagList prop。
              现在补齐:标签 chip + aria-pressed + 命中计数。 */}
          {tagList && tagList.length > 0 && (
            <div>
              <div className="mobile-filter-section-title">
                标签
                {filters.tag && (
                  <span style={{ color: 'var(--text-accent)', marginLeft: 8, fontSize: 12 }}>
                    · 已选 1 项
                  </span>
                )}
              </div>
              <div className="mobile-filter-chips tag-cloud">
                {tagCloudItems.map((t) => (
                  <button
                    key={t.key}
                    className={t.className}
                    onClick={t.onClick}
                    aria-pressed={t.isActive}
                    aria-label={t.ariaLabel}
                  >
                    {t.name}
                    <span style={{ opacity: 0.6, marginLeft: 4, fontSize: 11 }}>{t.count}篇</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Dates */}
          <div>
            <div className="mobile-filter-section-title">
              日期
              {filters.date && (
                <span style={{ color: 'var(--text-accent)', marginLeft: 8, fontSize: 12 }}>
                  · 已选 1 项
                </span>
              )}
            </div>
            <div className="mobile-filter-chips">
              {dateList.map(({ date, count }) => (
                <button
                  key={date}
                  className={`mobile-filter-chip ${filters.date === date ? 'active' : ''}`}
                  onClick={() => onFilterChange('date', filters.date === date ? '' : date)}
                  aria-pressed={filters.date === date}
                  aria-label={`${ContentService.formatDate(date)}，${count}篇`}
                >
                  {ContentService.formatDate(date)}
                  <span style={{ opacity: 0.6, marginLeft: 4, fontSize: 11 }}>{count}篇</span>
                </button>
              ))}
            </div>
          </div>

          {/* Clear */}
          {hasActiveFilter && (
            <button
              className="clear-filters"
              style={{ width: '100%', marginTop: 8 }}
              onClick={onClear}
              aria-label="清除全部筛选条件"
            >
              清除全部筛选
            </button>
          )}
        </div>
      </div>
    </>
  );
}

window.MobileFilterDrawer = MobileFilterDrawer;

export default MobileFilterDrawer;
